import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { MatchDetail } from '@brochure/shared'
import { phaseKey, phaseParams } from '@/lib/stoccopong-format'
import { cn } from '@/lib/utils'

type MatchHeaderProps = {
  match: MatchDetail['match']
  /** Freccia di ritorno (link del router): cambia tra arbitro e pagina pubblica. */
  back: ReactNode
  /** A destra della riga: i comandi del cronometro per l'arbitro. */
  actions?: ReactNode
  /** Al posto dello stato (in diretta / conclusa): la pagina pubblica ci mette il rimando alla Stockbet. */
  status?: ReactNode
  /** Il tabellone sotto la riga (Scoreboard). */
  children: ReactNode
  className?: string
}

/** Classe della freccia di ritorno: cerchio da 44px, come i comandi del cronometro. */
export const matchBackClass = 'grid size-11 shrink-0 place-items-center rounded-full text-paper transition-colors hover:bg-paper/10'

/**
 * Fascia sticky in cima alle schermate partita (variante notte): ritorno, fase e stato, comandi a destra e il
 * tabellone del punteggio, che resta in vista mentre si scorre il registro.
 */
export function MatchHeader({ match, back, actions, status, children, className }: MatchHeaderProps) {
  const { t } = useTranslation()
  return (
    <div className={cn('sticky z-30 border-b border-paper/10 bg-ink/95 backdrop-blur-md', className)}>
      <div className="mx-auto flex h-14 max-w-5xl items-center gap-2 px-2 sm:px-4">
        {back}
        <span className="truncate rounded-full border border-paper/25 px-2.5 py-1 font-body text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-paper/80">
          {t(phaseKey(match.phase), phaseParams(match.phase))}
        </span>
        {status}
        {!status && match.status === 'live' && (
          <span className="flex shrink-0 items-center gap-1.5 font-body text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-coral">
            <span className="size-1.5 rounded-full bg-coral motion-safe:animate-pulse" aria-hidden="true" />
            {t('stoccoPong.live')}
          </span>
        )}
        {!status && match.status === 'completed' && (
          <span className="shrink-0 font-body text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-paper/60">{t('stoccoPong.match.finished')}</span>
        )}
        <div className="ml-auto shrink-0">{actions}</div>
      </div>
      <div className="mx-auto max-w-5xl px-4 pb-4">{children}</div>
    </div>
  )
}
